import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, MapPin, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageBanner } from "@/components/PageBanner";
import { useTravelArticles } from "@/hooks/useTravelArticles";
import { getDefaultThumbnail } from "@/lib/strapi";

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45 } },
};

export default function TravelPage() {
  const {
    articles,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    loadMore,
  } = useTravelArticles(60);
  const [filterCountry, setFilterCountry] = useState("All");
  const fallbackThumbnail = getDefaultThumbnail();

  const handleImageError = (event) => {
    if (event.currentTarget.src !== fallbackThumbnail) {
      event.currentTarget.src = fallbackThumbnail;
    }
  };

  const getArticlePath = (article) => `/travel/${article?.slug || article?.id}`;
  const normalizeCountryName = (value) => String(value || "").trim().toLowerCase();
  const getArticleCountry = (article) => article?.location || "";

  const countries = [
    "All",
    ...Array.from(new Set(articles.map((article) => getArticleCountry(article)).filter(Boolean))),
  ];

  const filteredArticles = useMemo(() => {
    if (filterCountry === "All") {
      return articles;
    }

    const activeCountry = normalizeCountryName(filterCountry);
    return articles.filter((article) => normalizeCountryName(getArticleCountry(article)) === activeCountry);
  }, [articles, filterCountry]);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="travel-page" className="min-h-screen">
      <Header />
      <PageBanner
        eyebrow="Swim-Travel"
        title="Where Will You Swim Next?"
        description="Hand-picked swim destinations, heritage routes, and tropical escapes across Asia Pacific."
        image="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?crop=entropy&cs=srgb&fm=jpg&w=1920&q=80"
      />

      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {!isLoading && !error && articles.length > 0 && (
            <div className="mb-12">
              <label className="font-inter font-light text-xs tracking-[0.15em] uppercase text-warm-gray mb-3 block">
                Filter by Country
              </label>
              <div className="flex flex-wrap gap-2" data-testid="travel-country-filter">
                {countries.map((country) => (
                  <button
                    key={country}
                    type="button"
                    onClick={() => setFilterCountry(country)}
                    data-testid={`travel-filter-${country.toLowerCase().replace(/\s/g, "-")}`}
                    className={`font-inter font-medium text-xs px-4 py-2 rounded-sm border transition-colors duration-150 ${
                      filterCountry === country
                        ? "bg-solar-orange text-near-black border-solar-orange"
                        : "bg-transparent text-near-black/50 border-near-black/10 hover:border-near-black/30 hover:text-near-black"
                    }`}
                  >
                    {country}
                  </button>
                ))}
              </div>
            </div>
          )}

          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {isLoading && (
              <div className="md:col-span-2 lg:col-span-3 bg-white rounded-sm p-6 text-sm font-inter text-warm-gray">
                Loading travel articles...
              </div>
            )}

            {!!error && !isLoading && articles.length === 0 && (
              <div className="md:col-span-2 lg:col-span-3 bg-white rounded-sm p-6 text-sm font-inter text-red-600">
                Unable to load travel articles right now. Please try again shortly.
              </div>
            )}

            {!isLoading && !error && articles.length === 0 && (
              <div className="md:col-span-2 lg:col-span-3 bg-white rounded-sm p-6 text-sm font-inter text-warm-gray">
                No travel articles found.
              </div>
            )}

            {!isLoading && !error && articles.length > 0 && filteredArticles.length === 0 && (
              <div className="md:col-span-2 lg:col-span-3 bg-white rounded-sm p-6 text-sm font-inter text-warm-gray">
                No travel articles found for the selected country. Try a different country.
              </div>
            )}

            {!isLoading && filteredArticles.map((article) => (
              <motion.div key={article.id} variants={itemVariants}>
                <Link
                  to={getArticlePath(article)}
                  data-testid={`travel-dest-${article.id}`}
                  className="group bg-white rounded-sm overflow-hidden border border-near-black/[0.08] hover:-translate-y-2 transition-transform duration-300 h-full flex flex-col"
                >
                  <div className="relative overflow-hidden aspect-[16/11]">
                    <img
                      src={article.image}
                      alt={article.title}
                      loading="lazy"
                      onError={handleImageError}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <span className="absolute top-4 left-4 bg-solar-orange text-near-black font-inter font-semibold text-[11px] tracking-[0.1em] uppercase px-3 py-1 rounded-sm">
                      {article.category}
                    </span>
                  </div>
                  <div className="p-6 md:p-8 flex flex-col flex-1">
                    <div className="flex items-center gap-2 text-warm-gray mb-3">
                      <Clock size={12} />
                      <span className="font-inter font-light text-xs">{article.date}</span>
                    </div>
                    <h3 className="font-inter font-semibold text-xl text-near-black tracking-tight mb-2 group-hover:text-amber-dusk transition-colors duration-150">
                      {article.title}
                    </h3>
                    <div className="flex items-center gap-2 text-warm-gray mb-3">
                      <MapPin size={13} />
                      <span className="font-inter font-light text-xs">{article.location || "APAC"}</span>
                    </div>
                    <p className="font-inter font-normal text-sm text-warm-gray leading-relaxed flex-1 line-clamp-3">
                      {article.teaser}
                    </p>
                    <span className="inline-flex items-center gap-1 font-inter font-medium text-sm text-amber-dusk mt-5 group-hover:gap-2 transition-all duration-150">
                      Explore
                      <ArrowUpRight size={14} />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>

          {!isLoading && articles.length > 0 && (
            <div className="mt-10 flex flex-col items-center gap-4">
              {!!error && (
                <p className="font-inter text-sm text-red-600">
                  Unable to load more travel articles right now. Please try again shortly.
                </p>
              )}

              {hasMore && (
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-full bg-near-black px-6 py-3 font-inter text-sm text-white transition-colors duration-150 hover:bg-amber-dusk disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={loadMore}
                  disabled={isLoadingMore}
                >
                  {isLoadingMore ? "Loading more travel articles..." : "Load More Travel Articles"}
                </button>
              )}

              {!hasMore && !isLoadingMore && (
                <p className="font-inter text-sm text-warm-gray">
                  You’ve reached the end of the travel articles.
                </p>
              )}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
